type Tag = string;
interface Dim {
  width: number;
  height: number;
}
type CalculateResponse = Dim & {tag: Tag}
enum TAGS {
  ORIGINAL = "original",
  PORTRAIT = "portrait",
  TALL_PORTRAIT = "tall-portrait",
  SQUARE = "square",
  LANDSCAPE = "landscape",
  WIDE_LANDSCAPE = "wide-landscape"
}

export default class CheckAspectRatio {
  static isHD({ width, height }: Dim) {
    return width >= 1080 && height >= 1080;
  }

  static isPortrait({ width, height }: Dim) {
    return (width / height) > 0 && (width / height) <= 0.56;
  }

  static getPortrait(dim: Dim) {
    if (this.isHD(dim)) return { width: 1512, height: 1920, tag: TAGS.PORTRAIT };
    return { width: 1024, height: 1280, tag: TAGS.PORTRAIT };
  }

  static isTallPortrait({ width, height }: Dim) {
    return (width / height) > 0.56 && (width / height) <= 0.8;
  }

  static getTallPortrait(dim: Dim) {
    if (this.isHD(dim)) return { width: 1080, height: 1920, tag: TAGS.TALL_PORTRAIT };
    return { width: 720, height: 1280, tag: TAGS.TALL_PORTRAIT };
  }

  static isSquare({ width, height }: Dim) {
    return (width / height) > 0.8 && (width / height) <= 1;
  }

  static getSquare(dim: Dim) {
    if (this.isHD(dim)) return { width: 1920, height: 1920, tag: TAGS.SQUARE };
    return { width: 1280, height: 1280, tag: TAGS.SQUARE };
  }

  static isLandscape({ width, height }: Dim) {
    return (width / height) > 1 && (width / height) <= 1.35;
  }

  static getLandscape(dim: Dim) {
    if (this.isHD(dim)) return { width: 1920, height: 1512, tag: TAGS.LANDSCAPE };
    return { width: 1280, height: 1024, tag: TAGS.LANDSCAPE };
  }

  static isWideLandscape({ width, height }: Dim) {
    return (width / height) > 1.35;
  }

  static getWideLandscape(dim: Dim) {
    if (this.isHD(dim)) return { width: 1920, height: 1080, tag: TAGS.WIDE_LANDSCAPE };
    return { width: 1280, height: 720, tag: TAGS.WIDE_LANDSCAPE };
  }

  static getOriginal(dim: Dim) {
    if (this.isHD(dim)) return { width: 1920, height: 1080, tag: TAGS.ORIGINAL };
    return { width: 1280, height: 720, tag: TAGS.ORIGINAL };
  }

  static getResolutionByTag(tag: Tag, dim: Dim) {
    return ({
      [TAGS.ORIGINAL]: () => this.getOriginal(dim),
      [TAGS.PORTRAIT]: () => this.getPortrait(dim),
      [TAGS.TALL_PORTRAIT]: () => this.getTallPortrait(dim),
      [TAGS.SQUARE]: () => this.getSquare(dim),
      [TAGS.LANDSCAPE]: () => this.getLandscape(dim),
      [TAGS.WIDE_LANDSCAPE]: () => this.getWideLandscape(dim)
    } as Record<string, () => CalculateResponse>)[tag]();
  }

  static calculate(dim: Dim, tag?: Tag) {
    if (tag) return this.getResolutionByTag(tag, dim);

    if (this.isPortrait(dim)) return this.getPortrait(dim);
    if (this.isTallPortrait(dim)) return this.getTallPortrait(dim);
    if (this.isSquare(dim)) return this.getSquare(dim);
    if (this.isLandscape(dim)) return this.getLandscape(dim);
    if (this.isWideLandscape(dim)) return this.getWideLandscape(dim);
    return this.getOriginal(dim);
  }
}
